import React from 'react'
import { motion } from 'framer-motion'
import CRYN_BADGE from '../../../../app/assets/images/projectImage/cryoneimage/CRYN.webp'
import CRYONE_IMAGE from '../../../../app/assets/images/projectImage/cryoneimage/ANIMATION_IMAGE.webp'
import BG1 from '../../../../app/assets/images/projectImage/cryoneimage/bg1.svg'

type CryoneBannerProps = {
    cryoneProject: any
}

const CryoneBanner = ({ cryoneProject }: CryoneBannerProps) => {
    const { cryoneProjectDetails } = cryoneProject ?? {}
    const { bannerSection } = cryoneProjectDetails ?? {}
    const titlePrefix = bannerSection?.titlePrefix
    const titleSuffix = bannerSection?.titleSuffix
    const tokenLabel = bannerSection?.tokenLabel
    const subtitle = bannerSection?.subtitle

    return (
        <>
            <style>{`
                @keyframes slowRevolve {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .slow-revolve {
                    animation: slowRevolve 30s linear infinite;
                }

                .cryone-wordmark {
                    font-family: "Arial Black", "Helvetica Neue", Arial, sans-serif;
                    font-weight: 900;
                    letter-spacing: -0.06em;
                    line-height: 0.9;
                }
            `}</style>

            <section
                className="relative overflow-hidden min-h-[70vh] sm:min-h-[80vh] lg:min-h-[90vh] flex items-center"
                style={{
                    backgroundImage: `url(${BG1})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                
                {/* Background Glows */}
                <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(255,0,0,0.05)_0%,rgba(255,0,0,0)_45%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,44,44,0.24),rgba(0,0,0,0)_56%)]" />

                <div className="relative w-full max-w-[1500px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-16 py-16 sm:py-20 lg:py-24">
                        
                        {/* LEFT CONTENT */}
                        <div className="w-full lg:w-[55%] flex flex-col items-center lg:items-start lg:-translate-x-6">
                            
                            {/* Title and Badge Container */}
                            <div className="relative flex flex-col items-center w-fit">

                                {/* Main "CRYONE" Title */}
                                <motion.div
                                    className="relative flex items-end justify-center gap-0.5 sm:gap-1 md:gap-2"
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                >
                                    <span
                                        className="cryone-wordmark text-[56px] sm:text-[78px] md:text-[104px] lg:text-[128px] xl:text-[148px] text-[#F3F2F7]"
                                        style={{
                                            textShadow: '0 2px 0 rgba(0,0,0,0.25), 0 0 28px rgba(255,255,255,0.16)'
                                        }}
                                    >
                                        {titlePrefix}
                                    </span>
                                    <span
                                        className="cryone-wordmark text-[56px] sm:text-[78px] md:text-[104px] lg:text-[128px] xl:text-[148px] text-[#FF5A4D]"
                                        style={{
                                            textShadow: '0 0 10px rgba(255,90,77,0.9), 0 0 28px rgba(255,74,58,0.65), 0 0 54px rgba(255,53,38,0.55)'
                                        }}
                                    >
                                        {titleSuffix}
                                    </span>
                                </motion.div>

                                {/* Token Badge */}
                                <motion.div
                                    className="relative z-20 mt-4 sm:mt-5 md:mt-6"
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.6, delay: 0.3, type: "spring", stiffness: 100 }}
                                >
                                    <img
                                        src={CRYN_BADGE}
                                        alt={tokenLabel}
                                        className="w-[75px] sm:w-[95px] md:w-[110px] lg:w-[125px] drop-shadow-[0_0_20px_rgba(255,0,0,0.6)]"
                                    />
                                </motion.div>
                            </div>

                            {/* Subtext */}
                            <motion.p
                                className="text-[#F4F4F5] text-2xl sm:text-[28px] md:text-[34px] lg:text-[36px] leading-[1.2] max-w-[820px] font-medium text-center mt-8 sm:mt-10 z-10"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.4 }}
                            >
                                {subtitle}
                            </motion.p>
                        </div>

                        {/* RIGHT SIDE - ROTATING IMAGE */}
                        <motion.div
                            className="w-full lg:w-[45%] flex justify-center lg:justify-end mt-6 sm:mt-10 lg:mt-0 lg:translate-x-6"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, delay: 0.2 }}
                        >
                            <div className="relative w-[220px] sm:w-[220px] md:w-[420px] lg:w-[580px] xl:w-[640px] aspect-square flex items-center justify-center">
                                <div className="w-full h-full slow-revolve">
                                    <img
                                        src={CRYONE_IMAGE}
                                        alt="Cryone Visual"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-red-600/20 blur-[100px] rounded-full -z-10" />
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>
        </>
    )
}

export default CryoneBanner
